/**
 * Utilitaire de vérification de version
 *
 * Vérifie périodiquement si une nouvelle version est disponible
 * et force le rechargement de la page si nécessaire.
 */

import { API_ENDPOINTS } from './apiConfig';
import packageInfo from '../../package.json';

// Version actuelle de l'application (depuis package.json)
const CURRENT_VERSION = packageInfo.version;

// Intervalle de vérification (5 minutes)
const CHECK_INTERVAL = 5 * 60 * 1000;

// Délai avant reload après notification (3 secondes)
const RELOAD_DELAY = 3000;

let checkInterval = null;
let lastKnownVersion = CURRENT_VERSION;

/**
 * Vérifie si une nouvelle version est disponible
 * Utilise /api/version (route publique légère) au lieu de /api/versions
 * @returns {Promise<{hasUpdate: boolean, serverVersion: string|null}>}
 */
export const checkVersion = async () => {
  try {
    const response = await fetch(API_ENDPOINTS.VERSION, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      // Pas de cache pour cette requête
      cache: 'no-store'
    });

    if (!response.ok) {
      return { hasUpdate: false, serverVersion: null };
    }

    const data = await response.json();

    if (data.success && data.version) {
      const serverVersion = data.version;

      // Comparer avec la version actuelle
      if (serverVersion !== CURRENT_VERSION && serverVersion !== 'unknown') {
        console.info(`🔄 Nouvelle version détectée: ${serverVersion} (actuelle: ${CURRENT_VERSION})`);
        return { hasUpdate: true, serverVersion };
      }
    }

    return { hasUpdate: false, serverVersion: null };
  } catch (error) {
    // Silencieux - ne pas bloquer l'application
    console.warn('Version check failed:', error.message);
    return { hasUpdate: false, serverVersion: null };
  }
};

/**
 * Force le rechargement de la page après notification
 * @param {string} newVersion - La nouvelle version disponible
 * @param {function} showNotification - Fonction pour afficher une notification
 */
export const forceReload = (newVersion, showNotification) => {
  if (showNotification) {
    showNotification(
      `Nouvelle version ${newVersion} disponible. Rechargement automatique...`,
      'info'
    );
  }

  // Attendre un peu pour que l'utilisateur voit le message
  setTimeout(() => {
    // Forcer un rechargement complet (pas depuis le cache)
    window.location.reload(true);
  }, RELOAD_DELAY);
};

/**
 * Démarre la vérification périodique de version
 * @param {function} onNewVersion - Callback appelé quand une nouvelle version est détectée
 * @param {function} showNotification - Fonction pour afficher une notification (optionnel)
 */
export const startVersionCheck = (onNewVersion, showNotification) => {
  // Arrêter tout intervalle existant
  stopVersionCheck();

  console.info(`📦 Version check démarré (version actuelle: ${CURRENT_VERSION})`);

  // Vérification périodique
  checkInterval = setInterval(async () => {
    const { hasUpdate, serverVersion } = await checkVersion();

    if (hasUpdate && serverVersion) {
      // Ne notifier qu'une fois par version
      if (serverVersion !== lastKnownVersion) {
        lastKnownVersion = serverVersion;

        if (onNewVersion) {
          onNewVersion(serverVersion);
        } else {
          // Comportement par défaut: forcer le reload
          forceReload(serverVersion, showNotification);
        }
      }
    }
  }, CHECK_INTERVAL);

  // Première vérification après 30 secondes (laisser l'app se charger)
  setTimeout(async () => {
    const { hasUpdate, serverVersion } = await checkVersion();

    if (hasUpdate && serverVersion) {
      lastKnownVersion = serverVersion;

      if (onNewVersion) {
        onNewVersion(serverVersion);
      } else {
        forceReload(serverVersion, showNotification);
      }
    }
  }, 30000);
};

/**
 * Arrête la vérification périodique
 */
export const stopVersionCheck = () => {
  if (checkInterval) {
    clearInterval(checkInterval);
    checkInterval = null;
    console.info('📦 Version check arrêté');
  }
};

/**
 * Retourne la version actuelle de l'application
 */
export const getCurrentVersion = () => CURRENT_VERSION;

export default {
  checkVersion,
  forceReload,
  startVersionCheck,
  stopVersionCheck,
  getCurrentVersion
};
